import { useCallback, useMemo, useState } from 'react';
import produce from 'immer';
import { map, reduce, filter } from 'lodash-es';
import { useUpdateEffect } from 'ahooks';
import posthog from '@leyan/lytics';
/**
 * sku, 包括spu 合并，分离
 */
export type SpuLike = {
  internalItemId: string;
  [key: string]: any;
  skuInfo: {
    skuId: string;
    [key: string]: any;
  }[];
};

export type SpuSelectMap = Record<
  string,
  {
    checked: boolean;
    indeterminate: boolean;
    skus: Record<
      string,
      {
        checked: boolean;
      }
    >;
  }
>;

const convertSpusToMap: (spus: SpuLike[], initialSelectAll?: boolean) => SpuSelectMap = (
  spus,
  initialSelectAll = false,
) => {
  return reduce(
    spus,
    (acc, spu) => {
      acc[spu.internalItemId] = {
        checked: initialSelectAll,
        indeterminate: false,
        skus: reduce(
          spu?.skuInfo,
          (acc2, sku) => {
            acc2[sku.skuId] = {
              checked: initialSelectAll,
            };
            return acc2;
          },
          {} as SpuSelectMap[string]['skus'],
        ),
      };
      return acc;
    },
    {} as SpuSelectMap,
  );
};

export const convertMapToSpus: (spuSelectMap: SpuSelectMap) => SpuLike[] = (spuSelectMap) => {
  const result: SpuLike[] = [];
  Object.entries(spuSelectMap).forEach(([internalItemId, spu]) => {
    if (!spu.checked && spu.indeterminate) {
      result.push({
        internalItemId,
        skuInfo: Object.entries(spu.skus)
          .filter(([_, sku]) => sku.checked)
          .map(([skuId]) => {
            return {
              skuId,
            };
          }),
      });
    } else if (spu.checked && !spu.indeterminate) {
      result.push({
        internalItemId,
        skuInfo: Object.keys(spu.skus).map((skuId) => {
          return {
            skuId,
          };
        }),
      });
    }
  });
  return result;
};

export const convertMapToKcItems = (spuSelectMap: SpuSelectMap): KCItem[] => {
  return map(convertMapToSpus(spuSelectMap), (spu) => {
    const { internalItemId, skuInfo } = spu;
    return {
      itemInternalId: internalItemId,
      skus: skuInfo,
    };
  });
};

type SpuSelectSingle = {
  internalItemId: string;
  checked: boolean;
  indeterminate: boolean;
  skus: {
    [skuId: string]: {
      checked: boolean;
    };
  }[];
};
export const directConvertMapToArray = (spuSelectMap: SpuSelectMap): SpuSelectSingle[] => {
  return Object.entries(spuSelectMap).map(([internalItemId, spu]) => {
    const { skus, ...rest } = spu;
    return {
      internalItemId,
      ...rest,
      skus: Object.entries(skus).map(([skuId, other]) => {
        return {
          [skuId]: other,
        };
      }),
    };
  });
};
export const directConvertArrayToMap = (spus: SpuSelectSingle[]): SpuSelectMap => {
  return spus.reduce((acc, spu) => {
    const { internalItemId, skus, ...rest } = spu;
    acc[internalItemId] = {
      ...rest,
      skus: skus.reduce((acc2, sku) => {
        const [skuId, other] = Object.entries(sku)[0];
        acc2[skuId] = other;
        return acc2;
      }, {} as SpuSelectMap[string]['skus']),
    };
    return acc;
  }, {} as SpuSelectMap);
};

const pickSpuSelect = (largeMap: SpuSelectMap, smallMap: SpuSelectMap): SpuSelectMap => {
  const smallArray = directConvertMapToArray(smallMap);
  const newSmallArray = smallArray.map((each) => {
    const targetSpu = largeMap[each.internalItemId];
    if (!targetSpu) {
      return each;
    }
    each.skus = each.skus.map((sku) => {
      const [skuId, other] = Object.entries(sku)[0];
      const targetSku = targetSpu.skus?.[skuId];
      if (!targetSku) {
        return sku;
      }
      other.checked = targetSku.checked;
      return {
        [skuId]: other,
      };
    });
    const skuCheckedLength = each.skus.filter((sku) => Object.values(sku)[0].checked).length;
    if (each.skus.length === 0) {
      each.checked = targetSpu.checked;
      each.indeterminate = targetSpu.indeterminate;
      return each;
    }
    if (skuCheckedLength === 0) {
      each.checked = false;
      each.indeterminate = false;
    } else if (skuCheckedLength === each.skus.length) {
      each.checked = true;
      each.indeterminate = false;
    } else {
      each.indeterminate = true;
      each.checked = false;
    }
    return each;
  });

  return directConvertArrayToMap(newSmallArray);
};

export const useSelectControl = (
  spus: SpuLike[],
  defaultSpuSelectMap?: SpuSelectMap,
  options?: {
    initialSelectAll?: boolean;
    keepPrevSelected?: boolean;
  },
) => {
  const [spuSelectMap, setSpuSelectMap] = useState(() => {
    return defaultSpuSelectMap || convertSpusToMap(spus, !!options?.initialSelectAll);
  });
  useUpdateEffect(() => {
    const newSpuSelectMap = convertSpusToMap(spus, options?.initialSelectAll);
    setSpuSelectMap((spuSelectMap) => {
      return !!options?.keepPrevSelected
        ? pickSpuSelect(spuSelectMap, newSpuSelectMap)
        : convertSpusToMap(spus, options?.initialSelectAll);
    });
  }, [spus]);

  const toggleSku = useCallback(
    (internalItemId: string, skuId: string, checked: boolean) => {
      setSpuSelectMap(
        produce((spuSelectMap) => {
          const spu = spuSelectMap[internalItemId];
          if (!spu) {
            return;
          }
          const sku = spu.skus[skuId];
          if (!sku) {
            return;
          }
          sku.checked = checked;

          const skuLen = Object.values(spu.skus);
          const selectSkuLen = skuLen.filter((sku) => sku.checked).length;
          if (selectSkuLen > 0 && selectSkuLen < skuLen.length) {
            spu.indeterminate = true;
            spu.checked = false;
          }
          if (selectSkuLen === skuLen.length) {
            spu.indeterminate = false;
            spu.checked = true;
          }
          if (selectSkuLen === 0) {
            spu.indeterminate = false;
            spu.checked = false;
          }
          spuSelectMap[internalItemId] = spu;
        }),
      );
    },
    [setSpuSelectMap],
  );

  const toggleSkus = useCallback(
    (checked: boolean, spus: { internalItemId: string; skuId: string }[]) => {
      setSpuSelectMap((spuSelectMap) => {
        return produce(spuSelectMap, (draft) => {
          return spus.reduce((res, { internalItemId, skuId }) => {
            const spu = res[internalItemId];
            if (!spu) {
              return res;
            }
            const sku = spu.skus[skuId];
            if (!sku) {
              return res;
            }
            sku.checked = checked;

            const skuLen = Object.values(spu.skus);
            const selectSkuLen = skuLen.filter((sku) => sku.checked).length;
            if (selectSkuLen > 0 && selectSkuLen < skuLen.length) {
              spu.indeterminate = true;
              spu.checked = false;
            }
            if (selectSkuLen === skuLen.length) {
              spu.indeterminate = false;
              spu.checked = true;
            }
            if (selectSkuLen === 0) {
              spu.indeterminate = false;
              spu.checked = false;
            }
            return res;
          }, draft);
        });
      });
    },
    [],
  );

  const toggleSpu = useCallback(
    (internalItemId: string, checked: boolean) => {
      setSpuSelectMap(
        produce((spuSelectMap) => {
          const spu = spuSelectMap[internalItemId];
          if (!spu) {
            return;
          }
          spu.checked = checked;
          spu.indeterminate = false;
          Object.values(spu.skus).forEach((sku) => {
            sku.checked = checked;
          });
          spuSelectMap[internalItemId] = spu;
        }),
      );
    },
    [setSpuSelectMap],
  );

  const getBatchStatus = useCallback(
    (internalItemIds: string[] = []) => {
      let _checkedCount = 0;
      let _indeterminateCount = 0;
      if (internalItemIds.length === 0) {
        return {
          checked: false,
          indeterminate: false,
        };
      }
      internalItemIds.forEach((internalItemId) => {
        const spu = spuSelectMap[internalItemId];

        if (spu) {
          if (spu.checked) {
            _checkedCount++;
          } else if (spu.indeterminate) {
            _indeterminateCount++;
          }
        }
      });

      if (_checkedCount === internalItemIds.length && _indeterminateCount === 0) {
        return {
          checked: true,
          indeterminate: false,
        };
      }

      if (_checkedCount === 0 && _indeterminateCount === 0) {
        return {
          checked: false,
          indeterminate: false,
        };
      }

      return {
        checked: false,
        indeterminate: true,
      };
    },
    [spuSelectMap],
  );

  const toggleBatch = useCallback(
    (internalItemIds: string[], checked: boolean) => {
      setSpuSelectMap(
        produce((spuSelectMap) => {
          internalItemIds.forEach((internalItemId) => {
            const spu = spuSelectMap[internalItemId];
            if (!spu) {
              posthog.capture('$skuSelectError', {
                type: 'toggleBatch',
                data: {
                  internalItemId,
                },
              });
              return;
            }
            spu.checked = checked;
            spu.indeterminate = false;
            Object.values(spu.skus).forEach((sku) => {
              sku.checked = checked;
            });
          });
        }),
      );
    },
    [setSpuSelectMap],
  );

  // 统计选中的sku的个数
  const selectedSkuLen = useMemo(() => {
    return Object.values(spuSelectMap).reduce((acc, spu) => {
      const skuLen = Object.values(spu.skus);
      const selectSkuLen =
        skuLen.length > 0 ? skuLen.filter((sku) => sku.checked).length : spu.checked ? 1 : 0;
      return acc + selectSkuLen;
    }, 0);
  }, [spuSelectMap]);

  const cancelSelect = useCallback(() => {
    setSpuSelectMap(
      produce((spuSelectMap) => {
        for (const key in spuSelectMap) {
          spuSelectMap[key].checked = false;
          spuSelectMap[key].indeterminate = false;
          for (const key2 in spuSelectMap[key].skus) {
            const sku = spuSelectMap[key].skus[key2];
            if (sku) {
              sku.checked = false;
            }
          }
        }
      }),
    );
  }, [setSpuSelectMap]);

  return [
    spuSelectMap,
    selectedSkuLen,
    {
      toggleSku,
      toggleSkus,
      toggleSpu,
      toggleBatch,
      getBatchStatus,
      cancelSelect,
    },
  ] as const;
};

export type ToggleSku = ReturnType<typeof useSelectControl>[2]['toggleSku'];
export type ToggleSpu = ReturnType<typeof useSelectControl>[2]['toggleSpu'];
export type ToggleBatch = ReturnType<typeof useSelectControl>[2]['toggleBatch'];
export type GetBatchStatus = ReturnType<typeof useSelectControl>[2]['getBatchStatus'];
export type CancelSelect = ReturnType<typeof useSelectControl>[2]['cancelSelect'];

type SkuMap = Record<string, ItemSelectV2.Item['skuInfo'][number]>;
type SpuMap = Record<
  string,
  Omit<ItemSelectV2.Item, 'skuInfo'> & {
    skus: SkuMap;
  }
>;

/**
 * 将商品列表转为map, 这里是为了选择器的合并和分离
 */
export const convertAtoB = (spus: ItemSelectV2.Item[]) => {
  return spus.reduce((acc, spu) => {
    const { internalItemId, skuInfo, ...other } = spu;
    acc[internalItemId] = {
      internalItemId,
      ...other,
      skus: skuInfo.reduce((acc2, sku) => {
        acc2[sku.skuId] = sku;
        return acc2;
      }, {} as SkuMap),
    };
    return acc;
  }, {} as SpuMap);
};

const convertBtoA = (
  spus: Record<
    string,
    Omit<ItemSelectV2.Item, 'skuInfo'> & {
      skus: Record<string, ItemSelectV2.Item['skuInfo'][number]>;
    }
  >,
) => {
  return Object.values(spus).map((spu) => {
    const { internalItemId, skus, ...other } = spu;
    return {
      internalItemId,
      ...other,
      skuInfo: Object.values(skus),
    };
  });
};

/**
 * 逻辑同上，这里是因为知识库保存的数据结构特殊
 */
type KCItem = {
  itemInternalId: string;
  skus: {
    skuId: string;
    [k: string]: any;
  }[];
  [k: string]: any;
};
type KCItemMap = {
  [key: string]: Omit<KCItem, 'skus'> & {
    skus: Record<string, KCItem['skus'][number]>;
  };
};
const convertAtoBForKc = (spus: KCItem[]) => {
  return reduce(
    spus,
    (acc, spu) => {
      const { itemInternalId, skus, ...other } = spu;
      acc[itemInternalId] = {
        itemInternalId,
        ...other,
        skus: reduce(
          skus,
          (acc2, sku) => {
            acc2[sku.skuId] = sku;
            return acc2;
          },
          {} as KCItemMap[string]['skus'],
        ),
      };
      return acc;
    },
    {} as KCItemMap,
  );
};

const convertBtoAForKc = <T extends KCItem>(spus: KCItemMap): T[] => {
  return Object.values(spus).map((spu) => {
    const { itemInternalId, skus, ...other } = spu;
    return {
      itemInternalId,
      ...other,
      skus: Object.values(skus),
    } as T;
  });
};

export const splitSku: (sources: ItemSelectV2.Item[], apartMap: SpuSelectMap) => SpuLike[] = (
  sources,
  apartMap,
) => {
  const spuMap = convertAtoB(sources);
  const apart = convertMapToSpus(apartMap);
  return _splitSku(spuMap, apart);
};

const _splitSku = (spuMap: SpuMap, apart: SpuLike[]) => {
  for (let i = 0; i < apart.length; i++) {
    const spu = apart[i];
    const sourceSpu = spuMap[spu.internalItemId];
    if (!sourceSpu) continue;
    for (let j = 0; j < spu.skuInfo.length; j++) {
      const sku = spu.skuInfo[j];
      delete sourceSpu.skus[sku.skuId];
    }
    const sourceSkuLen = Object.keys(sourceSpu.skus).length;
    if (sourceSkuLen === 0) {
      delete spuMap[spu.internalItemId];
    }
  }
  return convertBtoA(spuMap);
};

export const splitSkuApart: (sources: ItemSelectV2.Item[], other: SpuLike[]) => SpuLike[] = (
  sources = [],
  other = [],
) => {
  const spuMap = convertAtoB(sources);
  return _splitSku(spuMap, other);
};

export const countSku: (spus: any[], key?: string) => number = (spus, key = 'skuInfo') => {
  return spus.reduce((acc, spu) => {
    return acc + (spu[key].length || 1);
  }, 0);
};

const convertSpuInfoToMap = (spus: ItemSelectV2.Item[]) => {
  return reduce(
    spus,
    (acc, spu) => {
      const { internalItemId, skuInfo, ...other } = spu;
      acc[internalItemId] = {
        internalItemId,
        ...other,
        skus: reduce(
          skuInfo,
          (acc2, sku) => {
            acc2[sku.skuId] = sku;
            return acc2;
          },
          {} as Record<string, ItemSelectV2.Item['skuInfo'][number]>,
        ),
      };
      return acc;
    },
    {} as Record<
      string,
      Omit<ItemSelectV2.Item, 'skuInfo'> & {
        skus: Record<string, ItemSelectV2.Item['skuInfo'][number]>;
      }
    >,
  );
};

export const getSpuInfoBySelectMap = <T extends ItemSelectV2.Item>(
  spus: T[],
  selectMap: SpuSelectMap,
): T[] => {
  const selectSpus = convertMapToSpus(selectMap);
  const spuMap = convertSpuInfoToMap(spus);
  return filter(
    map(selectSpus, (spu) => {
      const { internalItemId, skuInfo } = spu;
      const spu2 = spuMap[internalItemId];
      if (!spu2) return undefined as unknown as T;
      const { skus, ...other } = spu2;
      const skuInfo2 = skuInfo.map((sku) => {
        return skus[sku.skuId];
      });
      return {
        skuInfo: skuInfo2,
        ...other,
      } as T;
    }),
    Boolean,
  );
};

export const mergeItems = (part1: KCItem[], part2: KCItem[]) => {
  const map1 = convertAtoBForKc(part1);
  for (let i = 0; i < part2.length; i++) {
    const spu = part2[i];
    const spu2 = map1[spu.itemInternalId];
    if (spu2) {
      for (let j = 0; j < spu.skus?.length; j++) {
        const sku = spu.skus[j];
        spu2.skus[sku.skuId] = sku;
      }
    } else {
      const { itemInternalId, skus, ...other } = spu;
      map1[itemInternalId] = {
        ...other,
        itemInternalId,
        skus: reduce(
          skus,
          (acc2, sku) => {
            acc2[sku.skuId] = sku;
            return acc2;
          },
          {} as KCItemMap[string]['skus'],
        ),
      };
    }
  }

  return convertBtoAForKc(map1);
};

export const splitItems = <T extends KCItem>(part1: T[], part2: T[]): T[] => {
  const map1 = convertAtoBForKc(part1);
  for (let i = 0; i < part2.length; i++) {
    const spu = part2[i];
    const spu2 = map1[spu.itemInternalId];
    if (spu2) {
      for (let j = 0; j < spu.skus.length; j++) {
        const sku = spu.skus[j];
        delete spu2.skus[sku.skuId];
      }
      const skuLen = Object.keys(spu2.skus).length;
      if (skuLen === 0) {
        delete map1[spu.itemInternalId];
      }
    }
  }

  return convertBtoAForKc(map1);
};

export const convertItemsToKCItem = <T extends ItemSelectV2.Item>(items: T[]): KCItem[] => {
  return items.map((item) => {
    const { spuId, externalItemId, platform, region, internalItemId, skuInfo } = item;
    return {
      spuId: spuId || externalItemId,
      platform,
      region,
      itemInternalId: internalItemId,
      skus: skuInfo,
    };
  });
};

export const convertKCItemToItems = <T extends ItemSelectV2.Item>(items: KCItem[]): T[] => {
  return items.map((item) => {
    const { spuId, externalItemId, platform, region, itemInternalId, skus } = item;
    return {
      spuId: spuId || externalItemId,
      platform,
      region,
      internalItemId: itemInternalId,
      skuInfo: skus,
    } as T;
  });
};
